import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { useFetch } from "@/lib/useFetch";
import { usePageMeta } from "@/lib/usePageMeta";
import { useI18n } from "@/contexts/I18nContext";
import { GENRES } from "@/lib/constants";
import { BookCard } from "@/components/BookCard";
import { AuthorAvatar } from "@/components/AuthorAvatar";
import { SectionHeading } from "@/components/SectionHeading";
import { ErrorState } from "@/components/StateViews";
import { AdSlot } from "@/components/AdSlot";
import { FilterChip } from "@/components/ListLayout";

const GenreLinks = () => (
  <ul className="flex flex-wrap gap-2">
    {GENRES.map((g) => (
      <li key={g}>
        <Link to={`/books?genre=${encodeURIComponent(g)}`} className="inline-block rounded-full border border-border px-3.5 py-1.5 hover:border-primary hover:text-primary">{g}</Link>
      </li>
    ))}
  </ul>
);

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = (params.get("q") || "").trim();
  const genre = params.get("genre") || "";
  const { lang, t } = useI18n();
  const [draft, setDraft] = useState(q);
  useEffect(() => setDraft(q), [q]);

  const qs = new URLSearchParams({ q, limit: "12" });
  if (genre) qs.set("genre", genre);
  const { status, data, reload } = useFetch(q ? `/search?${qs}` : null);
  // Search result pages are for visitors, not for the index.
  usePageMeta({ title: q ? `${q} · ${t("nav_search")}` : t("nav_search"), noindex: true });

  const books = data?.books || [], authors = data?.authors || [], publishers = data?.publishers || [];
  const nothing = status === "ok" && !books.length && !authors.length && !publishers.length;
  const set = (next) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) { if (v) p.set(k, v); else p.delete(k); }
    setParams(p);
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <h1 className="text-[2rem] sm:text-[2.4rem]">{q ? t("search_results_for").replace("{q}", q) : t("nav_search")}</h1>
      <form role="search" onSubmit={(e) => { e.preventDefault(); set({ q: draft.trim() }); }}
        className="mt-4 flex max-w-2xl overflow-hidden rounded-[var(--radius)] border border-input">
        <label htmlFor="search-page-input" className="sr-only">{t("search_placeholder")}</label>
        <input id="search-page-input" value={draft} onChange={(e) => setDraft(e.target.value)}
          placeholder={t("search_placeholder")} className="h-12 min-w-0 flex-1 bg-card px-4 outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="search-page-input" />
        <button type="submit" aria-label={t("search_button")} className="flex items-center gap-2 bg-primary px-5 text-primary-foreground hover:bg-primary/90">
          <SearchIcon size={18} aria-hidden="true" /><span className="hidden sm:inline">{t("search_button")}</span>
        </button>
      </form>

      {q && (
        <fieldset className="mt-5">
          <legend className="sr-only">{t("filter_genre")}</legend>
          <ul className="flex flex-wrap gap-2" data-testid="search-genre-filter">
            <li><FilterChip active={!genre} onClick={() => set({ genre: "" })} testid="search-genre-all">{t("all")}</FilterChip></li>
            {GENRES.map((g) => (
              <li key={g}><FilterChip active={genre === g} onClick={() => set({ genre: g })} testid={`search-genre-${g}`}>{g}</FilterChip></li>
            ))}
          </ul>
        </fieldset>
      )}

      <div className="mt-8">
        {!q && (
          <div data-testid="search-empty" className="space-y-4">
            <p className="text-muted-foreground">{t("search_hint")}</p>
            <GenreLinks />
          </div>
        )}
        {status === "loading" && <div className="h-40 skel" />}
        {status === "error" && <ErrorState onRetry={reload} />}
        {nothing && (
          <div data-testid="search-zero" className="space-y-4">
            <p>{t("search_nothing").replace("{q}", q)}</p>
            <p className="text-muted-foreground">{t("search_try_genre")}</p>
            <GenreLinks />
          </div>
        )}
        {status === "ok" && !nothing && (
          <div className="space-y-12">
            {books.length > 0 && (
              <section>
                <SectionHeading title={`${t("nav_books")} (${books.length})`} />
                <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-4 lg:grid-cols-6 sm:gap-x-6">
                  {books.map((b) => <li key={b.id}><BookCard book={b} /></li>)}
                </ul>
              </section>
            )}
            {books.length > 0 && <AdSlot placement="search-results" />}
            {authors.length > 0 && (
              <section>
                <SectionHeading title={`${t("nav_authors")} (${authors.length})`} />
                <ul className="grid grid-cols-3 gap-x-4 gap-y-6 sm:grid-cols-6">
                  {authors.map((a) => (
                    <li key={a.id}>
                      <Link to={`/authors/${a.slug}`} className="group flex flex-col items-center text-center" data-testid={`search-author-${a.slug}`}>
                        <AuthorAvatar src={a.photo} name={a.name_hi} className="w-20" />
                        <span className="mt-2 leading-snug line-clamp-2 group-hover:text-primary">{lang === "hi" ? a.name_hi : (a.name_en || a.name_hi)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {publishers.length > 0 && (
              <section>
                <SectionHeading title={`${t("nav_publishers")} (${publishers.length})`} />
                <ul className="grid gap-x-8 sm:grid-cols-2">
                  {publishers.map((p) => (
                    <li key={p.id} className="border-b border-border">
                      <Link to={`/publishers/${p.slug}`} className="block py-3 hover:text-primary" data-testid={`search-publisher-${p.slug}`}>{p.name}</Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
